import {
	getFormProps,
	getInputProps,
	type SubmissionResult,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import * as E from '@react-email/components'
import {
	data,
	redirect,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link, useFetcher } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	GeneralErrorBoundary,
	statusHandlers,
} from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { checkForgotPasswordRateLimit } from '#app/utils/rate-limiting.server.ts'
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const ForgotPasswordSchema = z.object({
	usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
})

export const action = async ({ request }: ActionFunctionArgs) => {
	// Check rate limit before processing
	const rateLimitResponse = checkForgotPasswordRateLimit(request)
	if (rateLimitResponse) throw rateLimitResponse

	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const user = await prisma.user.findFirst({
				where: {
					provisional: false,
					OR: [
						{ email: data.usernameOrEmail },
						{ username: data.usernameOrEmail },
					],
				},
				select: { id: true },
			})
			if (!user) {
				ctx.addIssue({
					path: ['usernameOrEmail'],
					code: z.ZodIssueCode.custom,
					message: 'No user exists with this username or email',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { usernameOrEmail } = submission.value

	const user = await prisma.user.findFirstOrThrow({
		where: {
			provisional: false,
			OR: [{ email: usernameOrEmail }, { username: usernameOrEmail }],
		},
		select: { email: true, username: true },
	})

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'reset-password',
		target: usernameOrEmail,
	})

	const response = await sendEmail({
		to: user.email,
		subject: `Momentum Password Reset`,
		react: (
			<ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
		),
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return {
			result: response.error,
			status: 500,
		}
	}
}

function ForgotPasswordEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Momentum Password Reset</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Password Recovery for Momentum' }]
}

type ActionData = {
	result?: SubmissionResult<string[]>
}

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<ActionData>()

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<>
			<p className="text-eyebrow text-ember">Forgot password</p>
			<h1 className="mt-4 font-display text-display-2">
				We'll send you a reset link.
			</h1>
			<p className="mt-3 text-base leading-relaxed text-ink-fg-soft">
				Enter the username or email tied to your account.
			</p>

			<forgotPassword.Form
				method="POST"
				className="mt-10"
				{...getFormProps(form)}
			>
				<HoneypotInputs />
				<Field
					labelProps={{
						htmlFor: fields.usernameOrEmail.id,
						children: 'Username or email',
					}}
					inputProps={{
						autoFocus: true,
						...getInputProps(fields.usernameOrEmail, { type: 'text' }),
					}}
					errors={fields.usernameOrEmail.errors}
				/>
				<ErrorList errors={form.errors} id={form.errorId} />

				<StatusButton
					className="mt-6 w-full"
					status={
						forgotPassword.state === 'submitting'
							? 'pending'
							: (form.status ?? 'idle')
					}
					type="submit"
					disabled={forgotPassword.state !== 'idle'}
				>
					Send reset instructions
				</StatusButton>
			</forgotPassword.Form>

			<p className="mt-8 text-sm text-ink-fg-soft">
				<Link to="/login" className="ember-link">
					← Back to log in
				</Link>
			</p>
		</>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary statusHandlers={statusHandlers} />
}
